import { useState, useEffect, useCallback, useMemo } from 'react';
import { describePasswordFailure } from '@/lib/adminPasswordPolicy';

interface ValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  custom?: (value: string, values?: Record<string, string>) => string | null;
  dependencies?: string[]; // Fields that trigger re-validation of this field
}

interface ValidationRules {
  [key: string]: ValidationRule;
}

interface ValidationErrors {
  [key: string]: string | null;
}

interface FormState {
  values: Record<string, string>;
  errors: ValidationErrors;
  touched: Record<string, boolean>;
  isSubmitting: boolean;
  submitCount: number;
}

export function useFormValidation(
  initialValues: Record<string, string>,
  rules: ValidationRules,
  options: {
    validateOnChange?: boolean;
    validateOnBlur?: boolean;
    reValidateOnChange?: boolean;
  } = {}
) {
  const {
    validateOnChange = true,
    validateOnBlur = true,
    reValidateOnChange = true
  } = options;

  const [formState, setFormState] = useState<FormState>({
    values: { ...initialValues },
    errors: {},
    touched: {},
    isSubmitting: false,
    submitCount: 0
  });

  // Memoize field names for performance
  const fieldNames = useMemo(() => Object.keys(rules), [rules]);

  // Enhanced field validation with dependency support
  const validateField = useCallback((
    name: string, 
    value: string, 
    allValues: Record<string, string> = formState.values
  ): string | null => {
    const rule = rules[name];
    if (!rule) return null;

    // Required validation
    if (rule.required && !value.trim()) {
      return `${name.charAt(0).toUpperCase() + name.slice(1).replace(/([A-Z])/g, ' $1')} is required`;
    }

    // Skip other validations if field is empty and not required
    if (!value.trim() && !rule.required) {
      return null;
    }

    // Length validations
    if (rule.minLength && value.length < rule.minLength) {
      return `Must be at least ${rule.minLength} characters`;
    }

    if (rule.maxLength && value.length > rule.maxLength) {
      return `Must be no more than ${rule.maxLength} characters`;
    }

    // Pattern validation
    if (rule.pattern && !rule.pattern.test(value)) {
      return getPatternErrorMessage(name, rule.pattern);
    }

    // Custom validation
    if (rule.custom) {
      return rule.custom(value, allValues);
    }

    return null;
  }, [rules, formState.values]);

  // Helper function to get user-friendly pattern error messages
  const getPatternErrorMessage = (fieldName: string, pattern: RegExp): string => {
    const patternStr = pattern.toString();
    
    if (patternStr.includes('@') && patternStr.includes('\\.')) {
      return 'Please enter a valid email address';
    }
    
    if (patternStr.includes('[0-9]') || patternStr.includes('\\d')) {
      return 'Please enter a valid phone number';
    }
    
    return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} format is invalid`;
  };

  // Validate all fields
  const validateAll = useCallback((): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    fieldNames.forEach(name => {
      const error = validateField(name, formState.values[name] || '', formState.values);
      newErrors[name] = error;
      if (error) isValid = false;
    });

    setFormState(prev => ({
      ...prev,
      errors: newErrors,
      touched: fieldNames.reduce((acc, key) => ({ ...acc, [key]: true }), {})
    }));

    return isValid;
  }, [fieldNames, validateField, formState.values]);

  // Set field value with validation
  const setValue = useCallback((name: string, value: string) => {
    setFormState(prev => {
      const newValues = { ...prev.values, [name]: value };
      const newErrors = { ...prev.errors };

      // Validate current field if it's been touched or if validateOnChange is enabled
      if ((prev.touched[name] || validateOnChange) && reValidateOnChange) {
        newErrors[name] = validateField(name, value, newValues);
      }

      // Check for dependent fields and re-validate them
      const rule = rules[name];
      if (rule?.dependencies) {
        rule.dependencies.forEach(depField => {
          if (prev.touched[depField] || validateOnChange) {
            newErrors[depField] = validateField(depField, newValues[depField] || '', newValues);
          }
        });
      }

      // Also validate fields that depend on this field
      Object.keys(rules).forEach(fieldName => {
        const fieldRule = rules[fieldName];
        if (fieldRule?.dependencies?.includes(name)) {
          if (prev.touched[fieldName] || validateOnChange) {
            newErrors[fieldName] = validateField(fieldName, newValues[fieldName] || '', newValues);
          }
        }
      });

      return {
        ...prev,
        values: newValues,
        errors: newErrors
      };
    });
  }, [validateField, validateOnChange, reValidateOnChange, rules]);

  // Set multiple values at once
  const setValues = useCallback((newValues: Record<string, string>) => {
    setFormState(prev => {
      const updatedValues = { ...prev.values, ...newValues };
      const newErrors = { ...prev.errors };

      // Re-validate all affected fields
      Object.keys(newValues).forEach(name => {
        if (prev.touched[name] || validateOnChange) {
          newErrors[name] = validateField(name, updatedValues[name], updatedValues);
        }
      });

      return {
        ...prev,
        values: updatedValues,
        errors: newErrors
      };
    });
  }, [validateField, validateOnChange]);

  // Mark field as touched and validate
  const setFieldTouched = useCallback((name: string, shouldValidate = validateOnBlur) => {
    setFormState(prev => {
      const newTouched = { ...prev.touched, [name]: true };
      const newErrors = { ...prev.errors };

      if (shouldValidate) {
        newErrors[name] = validateField(name, prev.values[name] || '', prev.values);
      }

      return {
        ...prev,
        touched: newTouched,
        errors: newErrors
      };
    });
  }, [validateField, validateOnBlur]);

  // Set multiple fields as touched
  const setFieldsTouched = useCallback((fields: string[], shouldValidate = validateOnBlur) => {
    setFormState(prev => {
      const newTouched = { ...prev.touched };
      const newErrors = { ...prev.errors };

      fields.forEach(name => {
        newTouched[name] = true;
        if (shouldValidate) {
          newErrors[name] = validateField(name, prev.values[name] || '', prev.values);
        }
      });

      return {
        ...prev,
        touched: newTouched,
        errors: newErrors
      };
    });
  }, [validateField, validateOnBlur]);

  // Reset form to initial state
  const reset = useCallback((newInitialValues?: Record<string, string>) => {
    const resetValues = newInitialValues || initialValues;
    setFormState({
      values: { ...resetValues },
      errors: {},
      touched: {},
      isSubmitting: false,
      submitCount: 0
    });
  }, [initialValues]);

  // Set submitting state
  const setSubmitting = useCallback((isSubmitting: boolean) => {
    setFormState(prev => ({
      ...prev,
      isSubmitting,
      submitCount: isSubmitting ? prev.submitCount : prev.submitCount + 1
    }));
  }, []);

  // Get field properties for easy binding to form controls
  const getFieldProps = useCallback((name: string) => ({
    value: formState.values[name] || '',
    onChange: (value: string) => setValue(name, value),
    onBlur: () => setFieldTouched(name),
    error: formState.errors[name],
    touched: formState.touched[name],
    hasError: !!(formState.touched[name] && formState.errors[name])
  }), [formState.values, formState.errors, formState.touched, setValue, setFieldTouched]);

  // Computed values
  const isValid = useMemo(() => 
    Object.values(formState.errors).every(error => !error), 
    [formState.errors]
  );

  const isDirty = useMemo(() =>
    Object.keys(formState.values).some(key => 
      formState.values[key] !== (initialValues[key] || '')
    ),
    [formState.values, initialValues]
  );

  const hasErrors = useMemo(() =>
    Object.values(formState.errors).some(error => error !== null),
    [formState.errors]
  );

  const touchedFieldsCount = useMemo(() =>
    Object.values(formState.touched).filter(Boolean).length,
    [formState.touched]
  );

  return {
    // Form state
    values: formState.values,
    errors: formState.errors,
    touched: formState.touched,
    isSubmitting: formState.isSubmitting,
    submitCount: formState.submitCount,

    // Actions
    setValue,
    setValues,
    setFieldTouched,
    setFieldsTouched,
    setSubmitting,
    validateAll,
    validateField,
    reset,
    getFieldProps,

    // Computed state
    isValid,
    isDirty,
    hasErrors,
    touchedFieldsCount,

    // Helper functions
    clearErrors: () => setFormState(prev => ({ ...prev, errors: {} })),
    clearTouched: () => setFormState(prev => ({ ...prev, touched: {} })),
    setErrors: (errors: ValidationErrors) => setFormState(prev => ({ ...prev, errors })),
    
    // Check if specific field has error
    hasFieldError: (name: string) => !!(formState.touched[name] && formState.errors[name]),
    
    // Get first error message
    getFirstError: () => {
      const firstErrorField = fieldNames.find(name => 
        formState.touched[name] && formState.errors[name]
      );
      return firstErrorField ? formState.errors[firstErrorField] : null;
    }
  };
}

// Enhanced validation patterns and validators
export const validationPatterns = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  phone: /^\+?[\d\s\-().]{7,20}$/,
  phoneStrict: /^(\+\d{1,3}[- ]?)?\d{10,14}$/,
  url: /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/,
  alphanumeric: /^[a-zA-Z0-9]+$/,
  alphabetic: /^[a-zA-Z\s]+$/,
  numeric: /^[0-9]+$/,
  postalCode: /^[A-Z0-9]{3,10}$/i,
  strongPassword: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/
};

// Enhanced password validator with configurable requirements
// Password confirmation validator
export const createPasswordConfirmValidator = (passwordFieldName: string = 'password') =>
  (value: string, values?: Record<string, string>): string | null => {
    if (!values || value !== values[passwordFieldName]) {
      return 'Passwords do not match';
    }
    return null;
  };

// Email validator with domain checking
export const emailValidator = (value: string): string | null => {
  if (!validationPatterns.email.test(value)) {
    return 'Please enter a valid email address';
  }

  // Check for common typos in domain
  const domain = value.split('@')[1]?.toLowerCase();
  const commonDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com'];
  const suggestions: Record<string, string> = {
    'gmail.co': 'gmail.com',
    'yahoo.co': 'yahoo.com',
    'hotmail.co': 'hotmail.com',
    'gmial.com': 'gmail.com',
    'yahooo.com': 'yahoo.com'
  };

  if (domain && suggestions[domain]) {
    return `Did you mean ${value.split('@')[0]}@${suggestions[domain]}?`;
  }

  return null;
};

// Shared exports used by the authentication forms.
export const emailPattern = validationPatterns.email;
export const phonePattern = validationPatterns.phone;
export const passwordValidator = (value: string): string | null => describePasswordFailure(value);
